// =============================================================================
// Chemical element names, in both languages
// =============================================================================
//
// The database stores an element as its symbol — "Ga", not "Gallio". A name
// written into the database can never change language: user data does not pass
// through the translation table, so "Gallio" would stay Gallio for an English
// reader forever. Storing the symbol and translating here is the same approach
// the Trasferte map takes with country names.
//
// All 118 elements are listed, not only the 97 with a known radioisotope, so
// an element added by hand later is named rather than left as a bare symbol.
//
// `z` is the atomic number, used only for ordering: a chemist reading a list of
// elements expects periodic-table order, not alphabetical.
// =============================================================================

export const ELEMENTS = {
  H:  { z: 1,  it: "Idrogeno",     en: "Hydrogen" },
  He: { z: 2,  it: "Elio",         en: "Helium" },
  Li: { z: 3,  it: "Litio",        en: "Lithium" },
  Be: { z: 4,  it: "Berillio",     en: "Beryllium" },
  B:  { z: 5,  it: "Boro",         en: "Boron" },
  C:  { z: 6,  it: "Carbonio",     en: "Carbon" },
  N:  { z: 7,  it: "Azoto",        en: "Nitrogen" },
  O:  { z: 8,  it: "Ossigeno",     en: "Oxygen" },
  F:  { z: 9,  it: "Fluoro",       en: "Fluorine" },
  Ne: { z: 10, it: "Neon",         en: "Neon" },
  Na: { z: 11, it: "Sodio",        en: "Sodium" },
  Mg: { z: 12, it: "Magnesio",     en: "Magnesium" },
  Al: { z: 13, it: "Alluminio",    en: "Aluminium" },
  Si: { z: 14, it: "Silicio",      en: "Silicon" },
  P:  { z: 15, it: "Fosforo",      en: "Phosphorus" },
  S:  { z: 16, it: "Zolfo",        en: "Sulfur" },
  Cl: { z: 17, it: "Cloro",        en: "Chlorine" },
  Ar: { z: 18, it: "Argon",        en: "Argon" },
  K:  { z: 19, it: "Potassio",     en: "Potassium" },
  Ca: { z: 20, it: "Calcio",       en: "Calcium" },
  Sc: { z: 21, it: "Scandio",      en: "Scandium" },
  Ti: { z: 22, it: "Titanio",      en: "Titanium" },
  V:  { z: 23, it: "Vanadio",      en: "Vanadium" },
  Cr: { z: 24, it: "Cromo",        en: "Chromium" },
  Mn: { z: 25, it: "Manganese",    en: "Manganese" },
  Fe: { z: 26, it: "Ferro",        en: "Iron" },
  Co: { z: 27, it: "Cobalto",      en: "Cobalt" },
  Ni: { z: 28, it: "Nichel",       en: "Nickel" },
  Cu: { z: 29, it: "Rame",         en: "Copper" },
  Zn: { z: 30, it: "Zinco",        en: "Zinc" },
  Ga: { z: 31, it: "Gallio",       en: "Gallium" },
  Ge: { z: 32, it: "Germanio",     en: "Germanium" },
  As: { z: 33, it: "Arsenico",     en: "Arsenic" },
  Se: { z: 34, it: "Selenio",      en: "Selenium" },
  Br: { z: 35, it: "Bromo",        en: "Bromine" },
  Kr: { z: 36, it: "Kripton",      en: "Krypton" },
  Rb: { z: 37, it: "Rubidio",      en: "Rubidium" },
  Sr: { z: 38, it: "Stronzio",     en: "Strontium" },
  Y:  { z: 39, it: "Ittrio",       en: "Yttrium" },
  Zr: { z: 40, it: "Zirconio",     en: "Zirconium" },
  Nb: { z: 41, it: "Niobio",       en: "Niobium" },
  Mo: { z: 42, it: "Molibdeno",    en: "Molybdenum" },
  Tc: { z: 43, it: "Tecnezio",     en: "Technetium" },
  Ru: { z: 44, it: "Rutenio",      en: "Ruthenium" },
  Rh: { z: 45, it: "Rodio",        en: "Rhodium" },
  Pd: { z: 46, it: "Palladio",     en: "Palladium" },
  Ag: { z: 47, it: "Argento",      en: "Silver" },
  Cd: { z: 48, it: "Cadmio",       en: "Cadmium" },
  In: { z: 49, it: "Indio",        en: "Indium" },
  Sn: { z: 50, it: "Stagno",       en: "Tin" },
  Sb: { z: 51, it: "Antimonio",    en: "Antimony" },
  Te: { z: 52, it: "Tellurio",     en: "Tellurium" },
  I:  { z: 53, it: "Iodio",        en: "Iodine" },
  Xe: { z: 54, it: "Xenon",        en: "Xenon" },
  Cs: { z: 55, it: "Cesio",        en: "Caesium" },
  Ba: { z: 56, it: "Bario",        en: "Barium" },
  La: { z: 57, it: "Lantanio",     en: "Lanthanum" },
  Ce: { z: 58, it: "Cerio",        en: "Cerium" },
  Pr: { z: 59, it: "Praseodimio",  en: "Praseodymium" },
  Nd: { z: 60, it: "Neodimio",     en: "Neodymium" },
  Pm: { z: 61, it: "Promezio",     en: "Promethium" },
  Sm: { z: 62, it: "Samario",      en: "Samarium" },
  Eu: { z: 63, it: "Europio",      en: "Europium" },
  Gd: { z: 64, it: "Gadolinio",    en: "Gadolinium" },
  Tb: { z: 65, it: "Terbio",       en: "Terbium" },
  Dy: { z: 66, it: "Disprosio",    en: "Dysprosium" },
  Ho: { z: 67, it: "Olmio",        en: "Holmium" },
  Er: { z: 68, it: "Erbio",        en: "Erbium" },
  Tm: { z: 69, it: "Tulio",        en: "Thulium" },
  Yb: { z: 70, it: "Itterbio",     en: "Ytterbium" },
  Lu: { z: 71, it: "Lutezio",      en: "Lutetium" },
  Hf: { z: 72, it: "Afnio",        en: "Hafnium" },
  Ta: { z: 73, it: "Tantalio",     en: "Tantalum" },
  W:  { z: 74, it: "Tungsteno",    en: "Tungsten" },
  Re: { z: 75, it: "Renio",        en: "Rhenium" },
  Os: { z: 76, it: "Osmio",        en: "Osmium" },
  Ir: { z: 77, it: "Iridio",       en: "Iridium" },
  Pt: { z: 78, it: "Platino",      en: "Platinum" },
  Au: { z: 79, it: "Oro",          en: "Gold" },
  Hg: { z: 80, it: "Mercurio",     en: "Mercury" },
  Tl: { z: 81, it: "Tallio",       en: "Thallium" },
  Pb: { z: 82, it: "Piombo",       en: "Lead" },
  Bi: { z: 83, it: "Bismuto",      en: "Bismuth" },
  Po: { z: 84, it: "Polonio",      en: "Polonium" },
  At: { z: 85, it: "Astato",       en: "Astatine" },
  Rn: { z: 86, it: "Radon",        en: "Radon" },
  Fr: { z: 87, it: "Francio",      en: "Francium" },
  Ra: { z: 88, it: "Radio",        en: "Radium" },
  Ac: { z: 89, it: "Attinio",      en: "Actinium" },
  Th: { z: 90, it: "Torio",        en: "Thorium" },
  Pa: { z: 91, it: "Protoattinio", en: "Protactinium" },
  U:  { z: 92, it: "Uranio",       en: "Uranium" },
  Np: { z: 93, it: "Nettunio",     en: "Neptunium" },
  Pu: { z: 94, it: "Plutonio",     en: "Plutonium" },
  Am: { z: 95, it: "Americio",     en: "Americium" },
  Cm: { z: 96, it: "Curio",        en: "Curium" },
  Bk: { z: 97, it: "Berkelio",     en: "Berkelium" },
  Cf: { z: 98, it: "Californio",   en: "Californium" },
  Es: { z: 99, it: "Einsteinio",   en: "Einsteinium" },
  Fm: { z: 100, it: "Fermio",      en: "Fermium" },
  Md: { z: 101, it: "Mendelevio",  en: "Mendelevium" },
  No: { z: 102, it: "Nobelio",     en: "Nobelium" },
  Lr: { z: 103, it: "Laurenzio",   en: "Lawrencium" },
  Rf: { z: 104, it: "Rutherfordio", en: "Rutherfordium" },
  Db: { z: 105, it: "Dubnio",      en: "Dubnium" },
  Sg: { z: 106, it: "Seaborgio",   en: "Seaborgium" },
  Bh: { z: 107, it: "Bohrio",      en: "Bohrium" },
  Hs: { z: 108, it: "Hassio",      en: "Hassium" },
  Mt: { z: 109, it: "Meitnerio",   en: "Meitnerium" },
  Ds: { z: 110, it: "Darmstadtio", en: "Darmstadtium" },
  Rg: { z: 111, it: "Roentgenio",  en: "Roentgenium" },
  Cn: { z: 112, it: "Copernicio",  en: "Copernicium" },
  Nh: { z: 113, it: "Nihonio",     en: "Nihonium" },
  Fl: { z: 114, it: "Flerovio",    en: "Flerovium" },
  Mc: { z: 115, it: "Moscovio",    en: "Moscovium" },
  Lv: { z: 116, it: "Livermorio",  en: "Livermorium" },
  Ts: { z: 117, it: "Tennesso",    en: "Tennessine" },
  Og: { z: 118, it: "Oganesson",   en: "Oganesson" },
};

/**
 * How an element reads in the interface: "Ga — Gallio".
 *
 * Anything that is not a known symbol comes back unchanged, so an element
 * somebody typed in by hand still shows what they typed rather than being
 * silently dropped.
 */
export function elementLabel(symbol, language = "it") {
  const entry = ELEMENTS[symbol];
  if (!entry) return symbol || "";
  return `${symbol} \u2014 ${entry[language] || entry.en}`;
}

/** Atomic number, or a large number so unknown symbols sort to the end. */
export function atomicNumber(symbol) {
  return ELEMENTS[symbol]?.z ?? 999;
}

/**
 * Filter a list of elements by symbol or by name in either language.
 *
 * Both languages at once on purpose: "gallium" should find Gallio while the
 * interface is in Italian. An exact symbol match sorts first, then names that
 * begin with the query — so typing "c" offers Carbonio before Calcio, and "ca"
 * offers Calcio before Californio.
 */
export function filterElements(elements, query, language = "it") {
  const q = (query || "").trim().toLowerCase();
  const sorted = [...elements].sort(
    (a, b) => atomicNumber(a.name) - atomicNumber(b.name)
  );
  if (!q) return sorted;

  const scored = [];
  for (const el of sorted) {
    const symbol = (el.name || "").toLowerCase();
    const entry = ELEMENTS[el.name];
    const it = (entry?.it || "").toLowerCase();
    const en = (entry?.en || "").toLowerCase();

    let rank = null;
    if (symbol === q) rank = 0;
    else if (it.startsWith(q) || en.startsWith(q) || symbol.startsWith(q)) rank = 1;
    else if (it.includes(q) || en.includes(q)) rank = 2;

    if (rank !== null) scored.push({ el, rank });
  }

  scored.sort((a, b) => a.rank - b.rank || atomicNumber(a.el.name) - atomicNumber(b.el.name));
  return scored.map((s) => s.el);
}
